import { createSelector } from 'reselect';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities, getListingsById } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { decodeHtmlEntities, formatHeightRange, formatSize } from '../../util/string';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { stringify } from '../../util/urlHelpers';
import { CLOTHING_SIZES, SHOE_SIZES } from '../../util/size';
import { BRANDS } from '../../config/product-constants';

export const REVIEWS_PER_PAGE = 100;

// ================ Action types ================ //

export const FETCH_REVIEWS_REQUEST = 'app/ReviewsPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/ReviewsPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/ReviewsPage/FETCH_REVIEWS_ERROR';

export const FETCH_REVIEW_REQUEST = 'app/ReviewsPage/FETCH_REVIEW_REQUEST';
export const FETCH_REVIEW_SUCCESS = 'app/ReviewsPage/FETCH_REVIEW_SUCCESS';
export const FETCH_REVIEW_ERROR = 'app/ReviewsPage/FETCH_REVIEW_ERROR';

export const FETCH_UNIQUE_BRANDS_REQUEST = 'app/ReviewsPage/FETCH_UNIQUE_BRANDS_REQUEST';
export const FETCH_UNIQUE_BRANDS_SUCCESS = 'app/ReviewsPage/FETCH_UNIQUE_BRANDS_SUCCESS';
export const FETCH_UNIQUE_BRANDS_ERROR = 'app/ReviewsPage/FETCH_UNIQUE_BRANDS_ERROR';

export const FETCH_AVAILABLE_REVIEW_FILTER_OPTIONS_REQUEST =
  'app/ReviewsPage/FETCH_AVAILABLE_REVIEW_FILTER_OPTIONS_REQUEST';
export const FETCH_AVAILABLE_REVIEW_FILTER_OPTIONS_SUCCESS =
  'app/ReviewsPage/FETCH_AVAILABLE_REVIEW_FILTER_OPTIONS_SUCCESS';
export const FETCH_AVAILABLE_REVIEW_FILTER_OPTIONS_ERROR =
  'app/ReviewsPage/FETCH_AVAILABLE_REVIEW_FILTER_OPTIONS_ERROR';

export const SET_SCROLL_POSITION = 'app/ReviewsPage/SET_SCROLL_POSITION';
export const SET_CURRENT_PAGE = 'app/ReviewsPage/SET_CURRENT_PAGE';
export const RESET_NAVIGATION_STATE = 'app/ReviewsPage/RESET_NAVIGATION_STATE';

// Helper to extract listing IDs (like SearchPage)
const resultIds = data => {
  const listings = data.data;
  return listings
    .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
    .map(l => l.id);
};

const initialState = {
  // Reviews list state
  reviewListingIds: [],
  reviewsInProgress: false,
  reviewsError: null,
  pagination: null,
  lastQueryParams: null,

  // Individual review state
  currentReview: null,
  currentReviewInProgress: false,
  currentReviewError: null,

  // Unique brands for filter dropdown
  uniqueBrands: [],
  uniqueBrandsInProgress: false,
  uniqueBrandsError: null,
  brandsInitialized: false,

  availableReviewFilterOptions: {
    heights: [],
    categories: [],
    clothingSizes: [],
    shoeSizes: [],
    brands: [],
  },
  availableReviewFilterOptionsInProgress: false,
  availableReviewFilterOptionsError: null,
  availableReviewFilterOptionsQueryKey: null,

  // Navigation state
  navigationState: {
    scrollPosition: 0,
    currentPage: 1,
    timestamp: null,
  },
};

export default function reviewsPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_REVIEWS_REQUEST:
      return {
        ...state,
        reviewsInProgress: true,
        lastQueryParams: payload.queryParams,
        reviewsError: null,
      };
    case FETCH_REVIEWS_SUCCESS:
      return {
        ...state,
        reviewsInProgress: false,
        reviewListingIds: resultIds(payload.data),
        pagination: payload.data.meta,
        reviewsError: null,
      };
    case FETCH_REVIEWS_ERROR:
      return {
        ...state,
        reviewsInProgress: false,
        reviewsError: payload,
      };

    case FETCH_REVIEW_REQUEST:
      return {
        ...state,
        currentReviewInProgress: true,
        currentReviewError: null,
      };
    case FETCH_REVIEW_SUCCESS:
      return {
        ...state,
        currentReviewInProgress: false,
        currentReview: payload,
        currentReviewError: null,
      };
    case FETCH_REVIEW_ERROR:
      return {
        ...state,
        currentReviewInProgress: false,
        currentReviewError: payload,
      };

    case FETCH_UNIQUE_BRANDS_REQUEST:
      return {
        ...state,
        uniqueBrandsInProgress: true,
        uniqueBrandsError: null,
      };
    case FETCH_UNIQUE_BRANDS_SUCCESS:
      return {
        ...state,
        uniqueBrandsInProgress: false,
        uniqueBrands: payload,
        uniqueBrandsError: null,
        brandsInitialized: true,
      };
    case FETCH_UNIQUE_BRANDS_ERROR:
      return {
        ...state,
        uniqueBrandsInProgress: false,
        uniqueBrandsError: payload,
      };

    case FETCH_AVAILABLE_REVIEW_FILTER_OPTIONS_REQUEST:
      return {
        ...state,
        availableReviewFilterOptionsInProgress: true,
        availableReviewFilterOptionsError: null,
        availableReviewFilterOptionsQueryKey: payload.queryKey,
      };
    case FETCH_AVAILABLE_REVIEW_FILTER_OPTIONS_SUCCESS:
      if (payload.queryKey !== state.availableReviewFilterOptionsQueryKey) {
        return state;
      }

      return {
        ...state,
        availableReviewFilterOptionsInProgress: false,
        availableReviewFilterOptions: payload.options,
        availableReviewFilterOptionsError: null,
      };
    case FETCH_AVAILABLE_REVIEW_FILTER_OPTIONS_ERROR:
      if (payload.queryKey !== state.availableReviewFilterOptionsQueryKey) {
        return state;
      }

      return {
        ...state,
        availableReviewFilterOptionsInProgress: false,
        availableReviewFilterOptionsError: payload.error,
      };

    case SET_SCROLL_POSITION:
      return {
        ...state,
        navigationState: {
          ...state.navigationState,
          scrollPosition: action.payload,
        },
      };

    case SET_CURRENT_PAGE:
      return {
        ...state,
        navigationState: {
          ...state.navigationState,
          currentPage: action.payload,
        },
      };

    case RESET_NAVIGATION_STATE:
      return {
        ...state,
        navigationState: {
          scrollPosition: 0,
          currentPage: 1,
          timestamp: null,
        },
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchReviewsRequest = () => ({
  type: FETCH_REVIEWS_REQUEST,
  payload: { queryParams: null },
});

export const fetchReviewsRequestWithParams = queryParams => ({
  type: FETCH_REVIEWS_REQUEST,
  payload: { queryParams },
});

export const fetchReviewsSuccess = response => ({
  type: FETCH_REVIEWS_SUCCESS,
  payload: { data: response.data },
});

export const fetchReviewsError = e => ({
  type: FETCH_REVIEWS_ERROR,
  error: true,
  payload: e,
});

export const fetchReviewRequest = () => ({
  type: FETCH_REVIEW_REQUEST,
});

export const fetchReviewSuccess = review => ({
  type: FETCH_REVIEW_SUCCESS,
  payload: review,
});

export const fetchReviewError = e => ({
  type: FETCH_REVIEW_ERROR,
  error: true,
  payload: e,
});

export const fetchUniqueBrandsRequest = () => ({
  type: FETCH_UNIQUE_BRANDS_REQUEST,
});

export const fetchUniqueBrandsSuccess = brands => ({
  type: FETCH_UNIQUE_BRANDS_SUCCESS,
  payload: brands,
});

export const fetchUniqueBrandsError = e => ({
  type: FETCH_UNIQUE_BRANDS_ERROR,
  error: true,
  payload: e,
});

export const fetchAvailableReviewFilterOptionsRequest = queryKey => ({
  type: FETCH_AVAILABLE_REVIEW_FILTER_OPTIONS_REQUEST,
  payload: { queryKey },
});

export const fetchAvailableReviewFilterOptionsSuccess = (queryKey, options) => ({
  type: FETCH_AVAILABLE_REVIEW_FILTER_OPTIONS_SUCCESS,
  payload: { queryKey, options },
});

export const fetchAvailableReviewFilterOptionsError = (queryKey, error) => ({
  type: FETCH_AVAILABLE_REVIEW_FILTER_OPTIONS_ERROR,
  payload: { queryKey, error },
});

export const setScrollPosition = position => ({
  type: SET_SCROLL_POSITION,
  payload: position,
});

export const setCurrentPage = page => ({
  type: SET_CURRENT_PAGE,
  payload: page,
});

export const resetNavigationState = () => ({
  type: RESET_NAVIGATION_STATE,
});

// ================ Thunks ================ //

export const fetchReviews = (queryParams = {}) => (dispatch, getState, sdk) => {
  dispatch(fetchReviewsRequestWithParams(queryParams));

  const localBrandName = queryParams.brandName || '';
  const restQueryParams = { ...queryParams };
  delete restQueryParams.sort;

  // Use exact same image configuration as SearchPage
  const variantPrefix = 'listing-card';
  const aspectRatio = 1; // Default aspect ratio

  // Use exact same image configuration as SearchPage but with standard variants
  const params = {
    // Filter for review-type listings
    pub_listingType: 'review',
    // Use perPage like SearchPage (not per_page)
    perPage: queryParams.perPage || REVIEWS_PER_PAGE,
    // Include basic data like SearchPage
    include: ['author', 'images'],
    // Use standard image variants that are always available
    'fields.image': ['variants.scaled-small', 'variants.scaled-medium'],
    'limit.images': 3, // Allow up to 3 images per listing
    // Additional filters can be added here
    ...restQueryParams,
    // Keep reviews in newest-first order.
    sort: 'createdAt',
  };

  delete params.brandName;

  if (localBrandName) {
    const requestedPage = Number(queryParams.page) || 1;
    const requestedPerPage = queryParams.perPage || REVIEWS_PER_PAGE;
    const fetchAllParams = {
      ...params,
      perPage: REVIEWS_PER_PAGE,
    };

    let page = 1;
    let totalPages = 1;
    const allListings = [];
    let lastMeta = null;

    const fetchAllPages = () => {
      return sdk.listings
        .query({ ...fetchAllParams, page })
        .then(response => {
          dispatch(addMarketplaceEntities(response));
          const listings = response.data?.data || [];
          totalPages = response.data?.meta?.totalPages || 1;
          lastMeta = response.data?.meta || null;
          allListings.push(...listings);
          page += 1;

          if (page <= totalPages && page <= 100) {
            return fetchAllPages();
          }

          const filteredListings = filterListingsByBrandName(allListings, localBrandName);
          const totalItems = filteredListings.length;
          const totalPagesForFilteredResults = Math.ceil(totalItems / requestedPerPage);
          const startIndex = (requestedPage - 1) * requestedPerPage;
          const pagedListings = filteredListings.slice(startIndex, startIndex + requestedPerPage);
          const syntheticResponse = {
            data: {
              data: pagedListings,
              meta: {
                ...(lastMeta || {}),
                page: requestedPage,
                perPage: requestedPerPage,
                totalItems,
                totalPages: totalPagesForFilteredResults,
              },
            },
          };

          dispatch(fetchReviewsSuccess(syntheticResponse));
          return syntheticResponse;
        })
        .catch(e => {
          dispatch(fetchReviewsError(storableError(e)));
          throw e;
        });
    };

    return fetchAllPages();
  }

  return sdk.listings
    .query(params)
    .then(response => {
      // Add listings to global marketplace entities store (like SearchPage)
      dispatch(addMarketplaceEntities(response));
      // Store just the listing references in our reducer
      dispatch(fetchReviewsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchReviewsError(storableError(e)));
      throw e;
    });
};

export const fetchReview = listingId => (dispatch, getState, sdk) => {
  dispatch(fetchReviewRequest());

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = {
    aspectWidth: 1,
    aspectHeight: 1,
    variantPrefix: 'listing-card',
  }; // Default config

  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      // Listing image variants
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  return sdk.listings
    .show(params)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const entities = denormalisedResponseEntities(response);
      const review = entities && entities.length > 0 ? entities[0] : null;
      dispatch(fetchReviewSuccess(review));
      return response;
    })
    .catch(e => {
      dispatch(fetchReviewError(storableError(e)));
      throw e;
    });
};

export const fetchUniqueBrands = () => async (dispatch, getState, sdk) => {
  dispatch(fetchUniqueBrandsRequest());

  try {
    const brandMap = new Map(); // Use Map to deduplicate by brandSlug but keep label

    // Add static brands first
    BRANDS.forEach(b => brandMap.set(b.value, { value: b.value, label: b.label }));

    let page = 1;
    let hasMore = true;
    const perPage = 100; // Maximum allowed per page

    while (hasMore) {
      const params = {
        pub_listingType: 'review',
        perPage,
        page,
        include: [], // Don't include images, author, etc.
        'fields.listing': ['publicData.BrandName', 'publicData.brandSlug'],
      };

      const response = await sdk.listings.query(params);
      const totalPages = response.data.meta.totalPages;
      const maxPages = totalPages - 9;

      if (maxPages < 0) {
        // brands from the last 8 pages are saved in static BRANDS array
        break;
      }
      const listings = response.data.data;

      listings.forEach(listing => {
        const brand = listing.attributes?.publicData?.BrandName;
        const brandSlug = listing.attributes?.publicData?.brandSlug;

        if (brand && brandSlug) {
          // Store with brandSlug as key to ensure uniqueness
          brandMap.set(brandSlug, {
            value: brandSlug,
            label: decodeHtmlEntities(brand),
          });
        }
      });

      // Check if there are more pages
      hasMore = listings.length === perPage;
      page++;

      if (page > 100) {
        console.warn('Reached maximum page limit for brand fetching');
        break;
      }
    }

    // Convert Map to array and sort alphabetically by label
    const uniqueBrands = Array.from(brandMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );

    dispatch(fetchUniqueBrandsSuccess(uniqueBrands));
    return uniqueBrands;
  } catch (e) {
    dispatch(fetchUniqueBrandsError(storableError(e)));
    throw e;
  }
};

const createEmptyAvailableReviewFilterOptions = () => ({
  heights: [],
  categories: [],
  clothingSizes: [],
  shoeSizes: [],
  brands: [],
});

const normalizeReviewBrandLabel = brandName =>
  decodeHtmlEntities(String(brandName || ''))
    .replace(/\s+/g, ' ')
    .trim();

const normalizeReviewBrandToken = brandName => normalizeReviewBrandLabel(brandName).toLowerCase();

const filterListingsByBrandName = (listings, brandName) => {
  const normalizedBrandName = normalizeReviewBrandToken(brandName);

  if (!normalizedBrandName) {
    return listings;
  }

  return listings.filter(listing => {
    const publicData = listing.attributes?.publicData || {};
    return normalizeReviewBrandToken(publicData.BrandName) === normalizedBrandName;
  });
};

const normalizeReviewHeightValue = height => {
  const normalizedHeight = formatHeightRange(height)
    .replace(/\s+/g, ' ')
    .trim();

  const aliases = {
    "<5'0": "<5'0",
    "5'0": "<5'0",
    '51-52': "5'1-5'2",
    '51-52-2': "5'1-5'2",
    '53-54': "5'3-5'4",
    '55-56': "5'5-5'6",
    '57-58': "5'7-5'8",
    '59-510': "5'9-5'10",
    "5'11": "5'11-6'0",
    "> 5'11": "5'11-6'0",
    ">5'11": "5'11-6'0",
  };

  return aliases[normalizedHeight] || normalizedHeight;
};

const normalizeOptionToken = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/_/g, '.')
    .replace(/^us(?=\d)/, 'us-')
    .replace(/^shoe-size-us-/, 'us-');

const findCanonicalSizeOption = (category, rawSize, formattedSize) => {
  const baseOptions = category === 'shoes' ? SHOE_SIZES : CLOTHING_SIZES;
  const candidates = [rawSize, formattedSize].filter(Boolean);

  for (const candidate of candidates) {
    const normalizedCandidate = normalizeOptionToken(candidate);
    const exactMatch = baseOptions.find(option => {
      const normalizedValue = normalizeOptionToken(option.value);
      const normalizedLabel = normalizeOptionToken(option.label);
      return normalizedCandidate === normalizedValue || normalizedCandidate === normalizedLabel;
    });

    if (exactMatch) {
      return exactMatch;
    }

    if (category === 'clothing' && /^\d+$/.test(normalizedCandidate)) {
      const usMatch = baseOptions.find(option => option.value === `US${normalizedCandidate}`);
      if (usMatch) {
        return usMatch;
      }
    }

    if (category === 'shoes' && /^\d+(\.\d+)?$/.test(normalizedCandidate)) {
      const usLabel = `US ${normalizedCandidate}`;
      const shoeMatch = baseOptions.find(option => option.label === usLabel);
      if (shoeMatch) {
        return shoeMatch;
      }
    }
  }

  return null;
};

const buildAvailableReviewFilterOptions = listings => {
  const heights = new Set();
  const categories = new Set();
  const clothingSizes = new Map();
  const shoeSizes = new Map();
  const brands = new Map();

  listings.forEach(listing => {
    const publicData = listing.attributes?.publicData || {};
    const category = publicData.categoryLevel1;
    const height = publicData.height;
    const brandSlug = publicData.brandSlug;
    const brandName = publicData.BrandName;
    const brandLabel = normalizeReviewBrandLabel(brandName);

    if (category) {
      categories.add(category);
    }

    if (height) {
      heights.add(normalizeReviewHeightValue(height));
    }

    if (brandLabel) {
      const brandKey = normalizeReviewBrandToken(brandLabel);
      const existingOption = brands.get(brandKey);
      const usesLocalBrandNameFilter = !brandSlug;
      const nextOption = usesLocalBrandNameFilter
        ? {
            value: `brand-name:${brandLabel}`,
            label: brandLabel,
            queryParam: 'brandName',
            queryValue: brandLabel,
          }
        : {
            value: brandSlug,
            label: brandLabel,
            queryParam: 'pub_brandSlug',
            queryValue: brandSlug,
          };

      if (!existingOption || usesLocalBrandNameFilter) {
        brands.set(brandKey, nextOption);
      }
    }

    const rawSize =
      publicData?.ClothingSize ||
      publicData?.ShoeSize ||
      publicData?.Size ||
      publicData?.productSize;
    const formattedSize = formatSize(publicData);
    if (!formattedSize || formattedSize === 'N/A' || !category) {
      return;
    }

    const sizeOption = findCanonicalSizeOption(category, rawSize, formattedSize);
    if (!sizeOption) {
      return;
    }

    if (category === 'shoes') {
      shoeSizes.set(sizeOption.value, sizeOption);
    } else if (category === 'clothing') {
      clothingSizes.set(sizeOption.value, sizeOption);
    }
  });

  return {
    heights: Array.from(heights).sort(),
    categories: Array.from(categories).sort(),
    clothingSizes: Array.from(clothingSizes.values()),
    shoeSizes: Array.from(shoeSizes.values()),
    brands: Array.from(brands.values()).sort((left, right) =>
      left.label.localeCompare(right.label)
    ),
  };
};

export const fetchAvailableReviewFilterOptions = (queryParams = {}) => async (
  dispatch,
  getState,
  sdk
) => {
  const normalizedQueryParams = Object.keys(queryParams).reduce((result, key) => {
    if (key !== 'page' && queryParams[key] != null && queryParams[key] !== '') {
      result[key] = queryParams[key];
    }
    return result;
  }, {});
  const localBrandName = normalizedQueryParams.brandName || '';
  const sdkQueryParams = { ...normalizedQueryParams };
  delete sdkQueryParams.brandName;
  const queryKey = stringify(normalizedQueryParams);
  const state = getState();
  const currentQueryKey = state.ReviewsPage?.availableReviewFilterOptionsQueryKey;
  const hasCachedOptions =
    Boolean(state.ReviewsPage?.availableReviewFilterOptions?.brands?.length) ||
    Boolean(state.ReviewsPage?.availableReviewFilterOptions?.categories?.length) ||
    Boolean(state.ReviewsPage?.availableReviewFilterOptions?.heights?.length) ||
    Boolean(state.ReviewsPage?.availableReviewFilterOptions?.clothingSizes?.length) ||
    Boolean(state.ReviewsPage?.availableReviewFilterOptions?.shoeSizes?.length);

  if (
    currentQueryKey === queryKey &&
    hasCachedOptions &&
    !state.ReviewsPage?.availableReviewFilterOptionsInProgress
  ) {
    return state.ReviewsPage.availableReviewFilterOptions;
  }

  dispatch(fetchAvailableReviewFilterOptionsRequest(queryKey));

  try {
    let page = 1;
    let totalPages = 1;
    const allListings = [];

    do {
      const response = await sdk.listings.query({
        pub_listingType: 'review',
        perPage: REVIEWS_PER_PAGE,
        page,
        include: [],
        'fields.listing': [
          'publicData.BrandName',
          'publicData.brandSlug',
          'publicData.height',
          'publicData.categoryLevel1',
          'publicData.ClothingSize',
          'publicData.ShoeSize',
          'publicData.Size',
          'publicData.productSize',
        ],
        ...sdkQueryParams,
      });

      const listings = response.data?.data || [];
      totalPages = response.data?.meta?.totalPages || 1;
      allListings.push(...listings);
      page += 1;
    } while (page <= totalPages && page <= 100);

    const listingsForOptions = localBrandName
      ? filterListingsByBrandName(allListings, localBrandName)
      : allListings;

    const availableOptions =
      listingsForOptions.length > 0
        ? buildAvailableReviewFilterOptions(listingsForOptions)
        : createEmptyAvailableReviewFilterOptions();

    dispatch(fetchAvailableReviewFilterOptionsSuccess(queryKey, availableOptions));
    return availableOptions;
  } catch (e) {
    const error = storableError(e);
    dispatch(fetchAvailableReviewFilterOptionsError(queryKey, error));
    throw error;
  }
};

// ================ Selectors ================ //
const getReviewIds = state => state.ReviewsPage?.reviewListingIds || [];

export const getReviews = createSelector(
  [getReviewIds, state => state],
  (reviewIds, state) => getListingsById(state, reviewIds)
);

export const getReviewsInProgress = state => state.ReviewsPage?.reviewsInProgress || false;
export const getReviewsError = state => state.ReviewsPage?.reviewsError || null;
export const getReviewsPagination = state => state.ReviewsPage.pagination;
export const getLastReviewsQueryParams = state => state.ReviewsPage?.lastQueryParams || null;

export const getCurrentReview = state => state.ReviewsPage.currentReview;
export const getCurrentReviewInProgress = state => state.ReviewsPage.currentReviewInProgress;
export const getCurrentReviewError = state => state.ReviewsPage.currentReviewError;

export const getUniqueBrands = state => state.ReviewsPage?.uniqueBrands || [];
export const getUniqueBrandsInProgress = state =>
  state.ReviewsPage?.uniqueBrandsInProgress || false;
export const getUniqueBrandsError = state => state.ReviewsPage?.uniqueBrandsError || null;
export const isBrandsInitialized = state => state.ReviewsPage?.brandsInitialized || false;
export const getAvailableReviewFilterOptions = state =>
  state.ReviewsPage?.availableReviewFilterOptions || createEmptyAvailableReviewFilterOptions();
export const getAvailableReviewFilterOptionsInProgress = state =>
  state.ReviewsPage?.availableReviewFilterOptionsInProgress || false;
export const getAvailableReviewFilterOptionsError = state =>
  state.ReviewsPage?.availableReviewFilterOptionsError || null;
export const getAvailableReviewFilterOptionsQueryKey = state =>
  state.ReviewsPage?.availableReviewFilterOptionsQueryKey || null;

export const selectNavigationState = state => state.ReviewsPage.navigationState;
export const selectScrollPosition = state => state.ReviewsPage.navigationState.scrollPosition;
export const selectCurrentPage = state => state.ReviewsPage.navigationState.currentPage;

// ================ Server-side data loading ================ //

/**
 * loadData function for server-side rendering
 * This is called on the server before rendering the page
 */
export const loadData = (params, search) => {
  const reviewId = params.id;

  if (!reviewId) {
    return Promise.resolve({});
  }

  return fetchReview(reviewId);
};
